// ABOUTME: Setup panel Facturatie view for super admin invoicing configuration, scoped to the
// ABOUTME: environment's customer: default invoice lines (shared codes, customer names/prices),
// ABOUTME: Asperion settings and customer-specific invoice lines.
import { Box, IconButton, useTheme } from "@mui/material";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectUser } from "../../../../../../newapi/user/authentication/authSlice";
import { tokens } from "../../../../../../theme";
import Subheading from "../../../../../../components/theme/text/Subheading";
import Text from "../../../../../../components/theme/text/Text";
import TextInput from "../../../../../../components/theme/inputs/TextInput";
import ShSwitch from "../../../../../../components/theme/inputs/ShSwitch";
import Shbutton from "../../../../../../components/theme/buttons/Shbutton";
import { InputDropdown } from "../../../../../../components/theme/dropdowns/InputDropdown";
import { useGetCustomersQuery } from "../../../../../../newapi/user/userSlice";
import { showToastMessageUpdateRequest, showToastMessageRequestError } from "../../../../../../utils/toasts";
import {
    useGetInvoicingConfigQuery,
    useUpdateInvoicingDefaultLinesMutation,
    useUpdateInvoicingCustomerMutation,
} from "../../../../../../newapi/financial/invoicingConfigSlice";

const inputBackground = (theme, colors) => theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100];

// The customer's invoicing settings: Asperion debtor info, base order amount and the
// customer-specific invoice lines, saved as one whole.
const CustomerInvoicingCard = ({ customer }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [updateCustomer] = useUpdateInvoicingCustomerMutation();

    const [companyCode, setCompanyCode] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [baseOrderAmount, setBaseOrderAmount] = useState('');
    const [customLines, setCustomLines] = useState([]);
    const [invoiceViaCustomerId, setInvoiceViaCustomerId] = useState(null);
    const [invoicingEnabled, setInvoicingEnabled] = useState(false);

    // Options for the "invoiced via" dropdown: own invoice first, then every other
    // production customer the redirect can point at. The /customers endpoint wraps the
    // list in { customers, success }.
    const { data: customersData } = useGetCustomersQuery();
    const allCustomers = Array.isArray(customersData) ? customersData : (customersData?.customers ?? []);
    const redirectOptions = [
        { id: null, name: t("invoicing_own_invoice") },
        ...allCustomers
            .filter(c => c.db_type === "production" && c.id !== customer.customerId)
            .map(c => ({ id: c.id, name: c.name })),
    ];
    const selectedRedirectIndex = redirectOptions.findIndex(option => option.id === invoiceViaCustomerId);

    useEffect(() => {
        setCompanyCode(customer.companyCode ?? '');
        setCompanyName(customer.companyName ?? '');
        setBaseOrderAmount(customer.baseOrderAmount ?? 0);
        setCustomLines(customer.customLines ?? []);
        setInvoiceViaCustomerId(customer.invoiceViaCustomerId ?? null);
        setInvoicingEnabled(customer.invoicingEnabled === true);
    }, [customer]);

    const setLineValue = (index, field, value) => {
        setCustomLines(lines => lines.map((line, i) => i === index ? { ...line, [field]: value } : line));
    };

    const addLine = () => {
        setCustomLines(lines => [...lines, { articleCode: '', description: '', amount: 1, price: '' }]);
    };

    const removeLine = (index) => {
        setCustomLines(lines => lines.filter((_, i) => i !== index));
    };

    const save = async () => {
        try {
            const response = await updateCustomer({
                customerId: customer.customerId,
                companyCode,
                companyName,
                baseOrderAmount,
                customLines,
                invoiceViaCustomerId,
                invoicingEnabled,
            }).unwrap();
            showToastMessageUpdateRequest(t, response);
        } catch (error) {
            showToastMessageRequestError(t("update_failed"), t, error);
        }
    };

    return (
        <Box
            sx={{
                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.bg["tertiary"],
                borderRadius: 4,
                padding: 3,
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Text semibold>{customer.name}</Text>
                    <Text light>{customer.db_catalog}</Text>
                </Box>
                <Shbutton variant="contained" color="primary" onClick={save}>{t("save")}</Shbutton>
            </Box>

            {/* Master switch: without it this customer is absent from the monthly invoicing
                mail and the Asperion export, also as redirect contributor */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Text semibold>{t("invoicing_enabled_title")}</Text>
                    <Text light>{t("invoicing_enabled_subtitle")}</Text>
                </Box>
                <ShSwitch
                    checked={invoicingEnabled}
                    onChange={(e) => { e.stopPropagation(); setInvoicingEnabled(e.target.checked); }}
                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                />
            </Box>

            {/* Invoiced via: own invoice, or all amounts stacked onto another customer's invoice */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Text light>{t("invoicing_invoice_via")}</Text>
                <Box sx={{ maxWidth: 300 }}>
                    <InputDropdown
                        displayName="name"
                        selected={selectedRedirectIndex}
                        options={redirectOptions}
                        noSelection={t("invoicing_own_invoice")}
                        onChange={(index) => setInvoiceViaCustomerId(redirectOptions[index]?.id ?? null)}
                        closeOnSelection
                    />
                </Box>
            </Box>

            {invoiceViaCustomerId !== null ? (
                <Text light>{t("invoicing_redirect_note")}</Text>
            ) : (
                <>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Text light>{t("invoicing_company_code")}</Text>
                            <TextInput background={inputBackground(theme, colors)} value={companyCode} onChange={(e) => setCompanyCode(e.target.value)} width={140}/>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1, minWidth: 200 }}>
                            <Text light>{t("invoicing_company_name")}</Text>
                            <TextInput background={inputBackground(theme, colors)} value={companyName} onChange={(e) => setCompanyName(e.target.value)}/>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Text light>{t("invoicing_base_order_amount")}</Text>
                            <TextInput background={inputBackground(theme, colors)} value={baseOrderAmount} onChange={(e) => setBaseOrderAmount(e.target.value.replace(/[^0-9]/g, ''))} width={140}/>
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <Text semibold>{t("invoicing_custom_lines_title")}</Text>
                        {customLines.map((line, index) => (
                            <Box key={index} sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                                <TextInput background={inputBackground(theme, colors)} placeholder={t("article_code")} value={line.articleCode ?? ''} onChange={(e) => setLineValue(index, 'articleCode', e.target.value)} width={140}/>
                                <TextInput background={inputBackground(theme, colors)} placeholder={t("description")} value={line.description ?? ''} onChange={(e) => setLineValue(index, 'description', e.target.value)} sx={{ flex: 1, minWidth: 200 }}/>
                                <TextInput background={inputBackground(theme, colors)} placeholder={t("amount")} value={line.amount ?? ''} onChange={(e) => setLineValue(index, 'amount', e.target.value.replace(/[^0-9]/g, ''))} width={90}/>
                                <TextInput background={inputBackground(theme, colors)} placeholder={t("price")} value={line.price ?? ''} onChange={(e) => setLineValue(index, 'price', e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.'))} width={110}/>
                                <IconButton onClick={() => removeLine(index)} size="small">
                                    <DeleteOutlinedIcon/>
                                </IconButton>
                            </Box>
                        ))}
                        <Box>
                            <Shbutton variant="outlined" color="primary" onClick={addLine} endIcon={<AddOutlinedIcon/>}>{t("add_line")}</Shbutton>
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );
};

const SetupPanelFacturatie = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // Superadmin (sha) only: the backend refuses non-sha callers, so without the flag we do
    // not fetch and render nothing. The setup route is sha-gated too; this guards the view
    // itself in case it is ever mounted elsewhere.
    const user = useSelector(selectUser);
    const isSuperAdmin = !!user?.sha;

    const { data: invoicingConfig } = useGetInvoicingConfigQuery(undefined, { skip: !isSuperAdmin });
    const [updateDefaultLines] = useUpdateInvoicingDefaultLinesMutation();

    const [defaultLines, setDefaultLines] = useState([]);

    useEffect(() => {
        if (invoicingConfig?.defaultLines) {
            setDefaultLines(invoicingConfig.defaultLines);
        }
    }, [invoicingConfig]);

    const setDefaultLineValue = (index, field, value) => {
        setDefaultLines(lines => lines.map((line, i) => i === index ? { ...line, [field]: value } : line));
    };

    const saveDefaultLines = async () => {
        try {
            const response = await updateDefaultLines(defaultLines.map(line => ({
                key: line.key,
                articleCode: line.articleCode,
                description: line.description,
                price: line.price,
            }))).unwrap();
            showToastMessageUpdateRequest(t, response);
        } catch (error) {
            showToastMessageRequestError(t("update_failed"), t, error);
        }
    };

    useEffect(() => {
        props.replaceView({
            ...props.config,
        });

        return () => {};
    }, []);

    if (!isSuperAdmin) {
        return null;
    }

    return (
        <Box
            sx={{
                height: 1,
                "@media screen and (max-width: 47.9375em)": {
                    paddingTop: 0,
                    paddingX: 0,
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: 1,
                    gap: 2,
                    overflow: "hidden",
                    background: theme.palette.mode === "dark" ? colors.grey[300] : colors.bg["tertiary"],
                    paddingX: 2,
                    paddingTop: 4,
                    height: 1,
                    position: "relative",
                    borderTopLeftRadius: 24,
                    borderTopRightRadius: 24,
                    "@media screen and (max-width: 47.9375em)": {
                        paddingTop: 0,
                        paddingX: 0,
                        background: colors.bg["tertiary"],
                    },
                }}
            >
                <Box
                    sx={{
                        paddingX: 3,
                        "@media screen and (max-width: 47.9375em)": {
                            display: "none",
                        },
                    }}
                >
                    <Subheading>{t("invoicing")}</Subheading>
                </Box>

                <Box
                    sx={{
                        background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                        pt: 3,
                        px: 3,
                        pb: 8,
                        overflow: "auto",
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                    }}
                >
                    {/* Default invoice lines: article codes are shared by all customers, the
                        name and price apply to this environment's customer. Hidden when the
                        customer is invoiced via another customer: that customer's prices apply. */}
                    {!invoicingConfig?.invoiceViaCustomerId && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                                <Box sx={{ display: "flex", flexDirection: "column" }}>
                                    <Text semibold>{t("invoicing_default_lines_title")}</Text>
                                    <Text light>{t("invoicing_default_lines_subtitle")}</Text>
                                </Box>
                                <Shbutton variant="contained" color="primary" onClick={saveDefaultLines}>{t("save")}</Shbutton>
                            </Box>
                            {defaultLines.map((line, index) => (
                                <Box key={line.key} sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                                    <Box sx={{ width: 200 }}>
                                        <Text>{t(`invoicing_line_${line.key}`)}</Text>
                                    </Box>
                                    <TextInput placeholder={t("description")} value={line.description ?? ''} onChange={(e) => setDefaultLineValue(index, 'description', e.target.value)} sx={{ flex: 1, minWidth: 200 }}/>
                                    <TextInput placeholder={t("article_code")} value={line.articleCode ?? ''} onChange={(e) => setDefaultLineValue(index, 'articleCode', e.target.value)} width={140}/>
                                    <TextInput placeholder={t("price")} value={line.price ?? ''} onChange={(e) => setDefaultLineValue(index, 'price', e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.'))} width={110}/>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Settings of the environment's customer */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Text semibold>{t("invoicing_customers_title")}</Text>
                            <Text light>{t("invoicing_customers_subtitle")}</Text>
                        </Box>
                        {invoicingConfig && (
                            <CustomerInvoicingCard customer={invoicingConfig}/>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default SetupPanelFacturatie;
